/* ==========================================================================
   TailStickle Theme — Main JavaScript
   ========================================================================== */

'use strict';

/* Opt into scroll-reveal ONLY when this script actually runs. If the JS fails
   to load/parse, .reveal-on is never set and all content stays visible. */
document.documentElement.classList.add('reveal-on');

/* -------------------------------------------------------------------------
   Utilities
   ------------------------------------------------------------------------- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, event, fn, opts) => el && el.addEventListener(event, fn, opts);
const off = (el, event, fn) => el && el.removeEventListener(event, fn);

function debounce(fn, ms = 200) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

function formatMoney(cents) {
  const amount = parseInt(cents, 10);
  if (isNaN(amount)) return '₹0.00';
  return '₹' + (amount / 100).toFixed(2);
}

/* -------------------------------------------------------------------------
   Header — scroll glassmorphism
   ------------------------------------------------------------------------- */
function initHeader() {
  const header = $('.site-header');
  if (!header) return;

  const heroSection = $('.hero');
  const isTransparentPage = heroSection && document.body.classList.contains('template-index');

  function update() {
    const scrolled = window.scrollY > 20;
    header.classList.toggle('is-scrolled', scrolled);
    if (isTransparentPage) header.classList.toggle('is-transparent', !scrolled);
  }

  update();
  on(window, 'scroll', update, { passive: true });
}

/* -------------------------------------------------------------------------
   Mobile Menu
   ------------------------------------------------------------------------- */
function initMobileMenu() {
  const menu = $('.mobile-menu');
  const overlay = $('#site-overlay');
  const openBtn = $('.hamburger-btn');
  const closeBtn = $('.mobile-menu__close');

  if (!menu) return;

  function open() {
    menu.classList.add('is-open');
    overlay.classList.add('is-visible');
    document.body.classList.add('no-scroll');
  }
  function close() {
    menu.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    document.body.classList.remove('no-scroll');
    // also close cart drawer if open
    $('.cart-drawer')?.classList.remove('is-open');
  }

  on(openBtn, 'click', open);
  on(closeBtn, 'click', close);
  on(overlay, 'click', close);
  on(document, 'keydown', e => e.key === 'Escape' && close());
}

/* -------------------------------------------------------------------------
   Cart Drawer
   ------------------------------------------------------------------------- */
const CartDrawer = {
  drawer: null,
  overlay: null,

  init() {
    this.drawer = $('.cart-drawer');
    this.overlay = $('#site-overlay');
    if (!this.drawer) return;

    $$('[data-cart-trigger]').forEach(btn => on(btn, 'click', () => this.open()));
    const closeBtn = this.drawer.querySelector('.cart-drawer__close');
    on(closeBtn, 'click', () => this.close());
    on(this.overlay, 'click', () => this.close());

    on(document, 'click', e => {
      if (e.target.closest('[data-add-to-cart]')) this.handleATC(e);
    });

    this.bindQtyButtons();
    this.fetchAndRender();
  },

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    document.body.classList.add('no-scroll');
    this.fetchAndRender();
  },

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    document.body.classList.remove('no-scroll');
    $('.mobile-menu')?.classList.remove('is-open');
  },

  async fetchAndRender() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.warn('Cart fetch failed:', e);
    }
  },

  render(cart) {
    const itemsEl   = this.drawer.querySelector('.cart-drawer__items');
    const emptyEl   = this.drawer.querySelector('.cart-drawer__empty');
    const footerEl  = this.drawer.querySelector('.cart-drawer__footer');
    const countEl   = this.drawer.querySelector('.cart-drawer__count');
    const subtotalEl   = this.drawer.querySelector('.cart-drawer__subtotal-price');
    const checkoutBtn  = this.drawer.querySelector('.cart-drawer__checkout');
    const shippingNote = this.drawer.querySelector('.cart-drawer__shipping-note');

    const itemCount  = parseInt(cart.item_count, 10) || 0;
    const totalPrice = cart.total_price || 0;

    // ── Count ──
    if (countEl) countEl.textContent = `(${itemCount} ${itemCount === 1 ? 'item' : 'items'})`;

    // ── Subtotal + Checkout button — keep both in sync with ₹ format ──
    if (subtotalEl) subtotalEl.textContent = formatMoney(totalPrice);
    if (checkoutBtn) checkoutBtn.textContent = `Checkout · ${formatMoney(totalPrice)}`;

    // ── Shipping note ──
    const FREE_SHIPPING_THRESHOLD = 3500; // paise — same as Liquid template
    if (shippingNote) {
      if (totalPrice >= FREE_SHIPPING_THRESHOLD) {
        shippingNote.textContent = '✓ You qualify for free shipping!';
      } else {
        shippingNote.textContent = `Add ${formatMoney(FREE_SHIPPING_THRESHOLD - totalPrice)} more for free shipping 🚚`;
      }
    }

    this.updateBadge(itemCount);

    // Use style.display throughout — no dependency on a .hidden CSS class
    if (emptyEl)  emptyEl.style.display  = itemCount === 0 ? '' : 'none';
    if (footerEl) footerEl.style.display = itemCount === 0 ? 'none' : '';

    if (itemCount === 0) {
      if (itemsEl) itemsEl.innerHTML = '';
      return;
    }

    if (!itemsEl) return;
    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__image"
          src="${item.image ? item.image.replace('.jpg', '_72x72.jpg') : ''}"
          alt="${item.title}" width="72" height="72" loading="lazy">
        <div>
          <div class="cart-item__name">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__qty">
            <button class="qty-btn" data-qty-change="-1" data-key="${item.key}" data-price="${item.price}" aria-label="Decrease">−</button>
            <span class="qty-value">${item.quantity}</span>
            <button class="qty-btn" data-qty-change="1" data-key="${item.key}" data-price="${item.price}" aria-label="Increase">+</button>
          </div>
        </div>
        <div>
          <div class="cart-item__price" data-line-price="${item.line_price}">${formatMoney(item.line_price)}</div>
          <button class="cart-item__remove" data-remove="${item.key}" aria-label="Remove item">×</button>
        </div>
      </div>
    `).join('');
    // NOTE: do NOT call bindQtyButtons() here — it uses event delegation on
    // the drawer element so the single listener added in init() covers all
    // dynamically-rendered buttons. Calling it again would stack listeners and
    // fire multiple simultaneous requests on every click.
  },

  bindQtyButtons() {
    const drawer = this.drawer;
    if (!drawer) return;

    // Guard: only attach once.
    if (drawer._qtyBound) return;
    drawer._qtyBound = true;

    // Lock flag prevents a second click while a request is in-flight.
    let _busy = false;

    on(drawer, 'click', async e => {
      if (_busy) return;

      const qtyBtn    = e.target.closest('[data-qty-change]');
      const removeBtn = e.target.closest('[data-remove]');

      if (qtyBtn) {
        const key    = qtyBtn.dataset.key;
        const change = parseInt(qtyBtn.dataset.qtyChange, 10);
        const cartItem = qtyBtn.closest('.cart-item');
        const qtyEl    = cartItem?.querySelector('.qty-value');
        const priceEl  = cartItem?.querySelector('.cart-item__price');
        if (!qtyEl) return;

        const current = parseInt(qtyEl.textContent, 10);
        if (isNaN(current)) return;
        const newQty  = Math.max(0, current + change);

        // ── OPTIMISTIC UPDATE — instant feedback, no waiting ──
        if (newQty === 0) {
          // Optimistically hide the row (will be confirmed by server render)
          cartItem.style.opacity = '0.4';
          cartItem.style.pointerEvents = 'none';
        } else {
          qtyEl.textContent = newQty;
          // Update line price instantly from stored unit price
          const unitPrice = parseInt(qtyBtn.dataset.price, 10);
          if (!isNaN(unitPrice) && priceEl) {
            priceEl.textContent = formatMoney(unitPrice * newQty);
          }
          // Recompute subtotal from all current line prices in DOM
          this._optimisticTotals(change);
        }

        _busy = true;
        try {
          await this.updateQty(key, newQty);
        } finally {
          _busy = false;
        }
      }

      if (removeBtn) {
        const cartItem = removeBtn.closest('.cart-item');
        if (cartItem) { cartItem.style.opacity = '0.4'; cartItem.style.pointerEvents = 'none'; }
        _busy = true;
        try {
          await this.updateQty(removeBtn.dataset.remove, 0);
        } finally {
          _busy = false;
        }
      }
    });
  },

  // Instantly update the subtotal + checkout button from the current DOM prices.
  _optimisticTotals(unitPriceChange) {
    const subtotalEl  = this.drawer.querySelector('.cart-drawer__subtotal-price');
    const checkoutBtn = this.drawer.querySelector('.cart-drawer__checkout');
    const shippingNote = this.drawer.querySelector('.cart-drawer__shipping-note');

    // Sum all line prices currently shown in the DOM
    let newTotal = 0;
    this.drawer.querySelectorAll('.cart-item__price').forEach(el => {
      // Use the freshly-updated text content
      const raw = el.textContent.replace(/[^0-9.]/g, '');
      newTotal += Math.round(parseFloat(raw) * 100) || 0;
    });

    if (subtotalEl)  subtotalEl.textContent  = formatMoney(newTotal);
    if (checkoutBtn) checkoutBtn.textContent  = `Checkout · ${formatMoney(newTotal)}`;
    const FREE = 3500;
    if (shippingNote) {
      shippingNote.textContent = newTotal >= FREE
        ? '✓ You qualify for free shipping!'
        : `Add ${formatMoney(FREE - newTotal)} more for free shipping 🚚`;
    }
  },

  async updateQty(key, qty) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      if (!res.ok) {
        // Shopify returned an error (e.g. 422 out-of-stock). Re-fetch the real
        // cart state instead of trying to parse the error body as a cart object.
        console.warn('Cart change failed with status', res.status);
        await this.fetchAndRender();
        return;
      }
      const cart = await res.json();
      // Sanity-check: the response must look like a real cart object.
      // If item_count is missing, Shopify returned something unexpected.
      if (typeof cart.item_count === 'undefined') {
        await this.fetchAndRender();
        return;
      }
      this.render(cart);
    } catch (e) {
      console.warn('Cart update failed:', e);
      // On network error, re-fetch so the drawer reflects the true cart state.
      await this.fetchAndRender();
    }
  },

  async handleATC(e) {
    e.preventDefault();
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn || btn._atcBusy) return;

    const form = btn.closest('form') || btn.closest('[data-product-form]');
    const variantId = form?.querySelector('[name="id"]')?.value || btn.dataset.variantId;
    if (!variantId) return;

    const qty = parseInt(form?.querySelector('[name="quantity"]')?.value || '1', 10);

    const originalHtml = btn.innerHTML;
    btn._atcBusy = true;
    btn.innerHTML = '✓ Adding…';
    btn.style.opacity = '0.75';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });
      if (!res.ok) throw new Error('ATC failed');
      // Open cart immediately, fetch real data in parallel
      this.open();
      this.fetchAndRender();
      btn.innerHTML = '✓ Added!';
      setTimeout(() => { btn.innerHTML = originalHtml; btn.style.opacity = ''; btn._atcBusy = false; }, 1500);
    } catch (err) {
      console.warn('Add to cart error:', err);
      btn.innerHTML = originalHtml;
      btn.style.opacity = '';
      btn._atcBusy = false;
    }
  },

  updateBadge(count) {
    $$('.cart-count-badge').forEach(badge => {
      badge.textContent = count;
      badge.classList.toggle('is-visible', count > 0);
    });
  }
};

/* -------------------------------------------------------------------------
   Scroll Reveal — Intersection Observer
   ------------------------------------------------------------------------- */
function initScrollReveal() {
  const items = $$('[data-reveal]');
  if (!items.length) return;

  // No IntersectionObserver support → just show everything.
  if (!('IntersectionObserver' in window)) {
    items.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -50px 0px' });

  items.forEach(el => observer.observe(el));

  // Fail-safe: reveal anything still hidden after 1.5s (covers observers that
  // never fire for above-the-fold content in some embedded/preview contexts).
  setTimeout(() => {
    items.forEach(el => el.classList.add('is-visible'));
  }, 1500);
}

/* -------------------------------------------------------------------------
   Product Gallery
   ------------------------------------------------------------------------- */
function initProductGallery() {
  const gallery = $('.product-gallery');
  if (!gallery) return;

  const mainImg = gallery.querySelector('.product-gallery__main-img');
  const thumbs = $$('.product-gallery__thumb', gallery);

  thumbs.forEach(thumb => {
    on(thumb, 'click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      if (mainImg) {
        mainImg.src = thumb.src.replace('_72x72', '_800x800').replace('_100x100', '_800x800');
        mainImg.srcset = '';
      }
    });
  });

  // Basic zoom on click
  if (mainImg) {
    on(mainImg, 'click', () => {
      const overlay = document.createElement('div');
      overlay.className = 'image-zoom-overlay';
      overlay.style.cssText = `
        position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.9);
        display:flex;align-items:center;justify-content:center;cursor:zoom-out;
      `;
      const img = document.createElement('img');
      img.src = mainImg.src;
      img.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;border-radius:12px;';
      overlay.appendChild(img);
      document.body.appendChild(overlay);
      on(overlay, 'click', () => overlay.remove());
    });
  }
}

/* -------------------------------------------------------------------------
   Product Page — Variant selection & sticky ATC bar
   ------------------------------------------------------------------------- */
function initSubscribeAndSave() {
  const sasOnetime    = document.getElementById('sasOnetime');
  const sasSubscribe  = document.getElementById('sasSubscribe');
  const sasBox        = document.getElementById('sasBox');
  const sellingPlanIn = document.getElementById('sellingPlanInput'); // name="selling_plan"
  const sasFreqHidden = document.getElementById('sasFreqHidden');    // fallback property
  const sasFreqSel    = document.getElementById('sasFrequency');
  if (!sasOnetime || !sasSubscribe) return;

  // Does this product have real Shopify selling plans (subscription app configured)?
  const hasPlans = sellingPlanIn && sellingPlanIn.dataset.hasPlans === 'true';

  const SAS_DISCOUNT = 0.15;
  let sasMode = 'subscribe';

  function updateSasDisplay(basePrice) {
    const subPrice = Math.round(basePrice * (1 - SAS_DISCOUNT));
    const otEl      = document.getElementById('sasOnetimePrice');
    const subEl     = document.getElementById('sasSubPrice');
    const crossedEl = document.getElementById('sasOnetimeCrossed');
    if (otEl)      otEl.textContent      = formatMoney(basePrice);
    if (subEl)     subEl.textContent     = formatMoney(subPrice);
    if (crossedEl) crossedEl.textContent = formatMoney(basePrice);
    const atcPriceEl = document.getElementById('atcPrice');
    if (atcPriceEl) atcPriceEl.textContent = formatMoney(sasMode === 'subscribe' ? subPrice : basePrice);
  }

  // Sync the hidden form fields to the current selection so the right
  // line item (one-time vs subscription selling plan) is added to cart → FBA/MCF.
  function syncFulfillmentFields() {
    const subscribing = sasMode === 'subscribe';
    if (hasPlans && sellingPlanIn) {
      // Real subscription: submit the chosen selling_plan id (empty = one-time)
      sellingPlanIn.value = subscribing ? (sasFreqSel ? sasFreqSel.value : '') : '';
      if (sasFreqHidden) sasFreqHidden.value = '';
    } else {
      // Fallback before subscription app is set up: record intent as a line-item property
      if (sellingPlanIn) sellingPlanIn.value = '';
      if (sasFreqHidden && sasFreqSel) sasFreqHidden.value = subscribing ? sasFreqSel.value : '';
    }
    // Keep Buy Now permalink in sync (variant:qty?selling_plan=ID)
    if (window._pdpSyncBuyNow) window._pdpSyncBuyNow();
  }

  function setMode(mode) {
    sasMode = mode;
    sasOnetime.classList.toggle('active',   mode === 'onetime');
    sasSubscribe.classList.toggle('active', mode === 'subscribe');
    sasBox.classList.toggle('subscribe-active', mode === 'subscribe');
    syncFulfillmentFields();
    const v = window._pdpCurrentVariant;
    if (v) updateSasDisplay(v.price);
  }

  sasOnetime.addEventListener('click',   () => setMode('onetime'));
  sasSubscribe.addEventListener('click', () => setMode('subscribe'));
  sasFreqSel && sasFreqSel.addEventListener('change', syncFulfillmentFields);

  // Expose for the variant switcher
  window._sasUpdatePrice = updateSasDisplay;
  window._sasIsSubscribe = () => sasMode === 'subscribe';
  window._sasSellingPlan = () => (hasPlans && sasMode === 'subscribe' && sasFreqSel) ? sasFreqSel.value : '';

  setMode('subscribe'); // subscribe is default (best value)
}

function initProductPage() {
  // ── Only run on product pages ──
  if (!document.querySelector('.pdp-wrap')) return;
  const product = window.productData;
  if (!product) return;

  // ── Build variant lookup: "option1|option2|option3" → variant object ──
  const variantMap = {};
  product.variants.forEach(v => {
    const key = [v.option1, v.option2, v.option3].filter(Boolean).join('|');
    variantMap[key] = v;
  });

  // ── Track selected option values, keyed by option position (1-based) ──
  const selectedOptions = {};
  $$('.pdp-variant-pill.active').forEach(pill => {
    selectedOptions[pill.dataset.optionPosition] = pill.dataset.value;
  });
  // If no active pills (single-variant product), default to first variant
  const optionCount = product.options ? product.options.length : 0;
  if (optionCount === 0 || product.has_only_default_variant) {
    // Single-variant — nothing to select
  }

  // ── Find variant from currently selected options ──
  function resolveVariant() {
    const keys = Object.keys(selectedOptions).sort();
    const combined = keys.map(k => selectedOptions[k]).join('|');
    return variantMap[combined] || product.variants[0];
  }

  // ── Update all UI elements for the given variant ──
  function applyVariant(variant) {
    if (!variant) return;
    window._pdpCurrentVariant = variant; // expose for SAS
    if (window._sasUpdatePrice) window._sasUpdatePrice(variant.price);

    const fmt = v => formatMoney(v); // uses existing helper in theme.js
    const avail = variant.available;

    // Hidden form input
    const idInput = document.getElementById('variantIdInput');
    if (idInput) idInput.value = variant.id;

    // ─ Price flows into the Subscribe & Save / one-time options ─
    if (window._sasUpdatePrice) window._sasUpdatePrice(variant.price);

    // ─ Stock status ─
    const inStockEl = document.getElementById('bbInStock');
    const oosEl     = document.getElementById('bbOos');
    const atcBtn    = document.getElementById('bbAtcBtn');
    const buyNow    = document.getElementById('buyNowBtn');

    if (avail) {
      inStockEl && (inStockEl.style.display = '');
      oosEl     && (oosEl.classList.remove('visible'));
      if (atcBtn) { atcBtn.disabled = false; atcBtn.childNodes[atcBtn.childNodes.length - 1].textContent = 'Add to Cart'; }
      if (buyNow) { buyNow.classList.remove('disabled'); }
    } else {
      inStockEl && (inStockEl.style.display = 'none');
      oosEl     && (oosEl.classList.add('visible'));
      if (atcBtn) { atcBtn.disabled = true; atcBtn.childNodes[atcBtn.childNodes.length - 1].textContent = 'Out of Stock'; }
      if (buyNow) { buyNow.classList.add('disabled'); }
    }

    // ─ Buy Now href (carry selling_plan for subscriptions → FBA/MCF recurring) ─
    if (window._pdpSyncBuyNow) window._pdpSyncBuyNow();

    // ─ Sticky bar price ─
    const sbPrice     = document.getElementById('stickyBarPrice');
    const sbAtcPrice  = document.getElementById('sbAtcPrice');
    if (sbPrice)    sbPrice.textContent    = fmt(variant.price);
    if (sbAtcPrice) sbAtcPrice.textContent = fmt(variant.price);

    // ─ Variant image — switch gallery if variant has its own image ─
    if (variant.featured_image) {
      const mainImg = document.getElementById('mainImg');
      if (mainImg) {
        mainImg.src = variant.featured_image.src;
        // Highlight matching thumbnail
        $$('.pdp-thumb').forEach(t => {
          t.classList.toggle('active', String(t.dataset.imgId) === String(variant.featured_image.id));
        });
      }
    }
  }

  // ── Mark OOS-only pills on page load ──
  product.options && product.options.forEach((optionName, idx) => {
    const position = String(idx + 1);
    const optionKey = `option${position}`;
    $$(`[data-option-position="${position}"]`).forEach(pill => {
      const value = pill.dataset.value;
      const hasAvailable = product.variants.some(v => v[optionKey] === value && v.available);
      if (!hasAvailable) pill.classList.add('oos');
    });
  });

  // ── Pill click handler ──
  $$('.pdp-variant-pill').forEach(pill => {
    on(pill, 'click', () => {
      const pos = pill.dataset.optionPosition;
      const val = pill.dataset.value;
      const optIdx = pill.dataset.optionIndex; // 0-based

      // Update active state within the same option group
      pill.closest('.pdp-variant-pills').querySelectorAll('.pdp-variant-pill')
        .forEach(p => p.classList.remove('active'));
      pill.classList.add('active');

      // Update selected value label (e.g. "Size: 150ml (Single)")
      const labelEl = document.getElementById(`selectedVal${parseInt(pos)}`);
      if (labelEl) labelEl.textContent = val;

      selectedOptions[pos] = val;
      applyVariant(resolveVariant());
    });

    // Keyboard accessibility
    on(pill, 'keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pill.click(); }
    });
  });

  // ── Buy box qty stepper ──
  let bbQty = 1;
  const bbMinus = document.getElementById('bbMinus');
  const bbPlus  = document.getElementById('bbPlus');
  const bbNum   = document.getElementById('bbNum');
  const qtyHidden = document.getElementById('qtyHidden');

  // Central Buy Now permalink builder — variant:qty(?selling_plan=ID)
  window._pdpSyncBuyNow = function () {
    const buyNow = document.getElementById('buyNowBtn');
    const v = resolveVariant();
    if (!buyNow || !v) return;
    const plan = window._sasSellingPlan ? window._sasSellingPlan() : '';
    let href = `/cart/${v.id}:${bbQty}`;
    if (plan) href += `?selling_plan=${plan}`;
    buyNow.href = href;
  };

  function updateBbQty(delta) {
    bbQty = Math.max(1, bbQty + delta);
    if (bbNum) bbNum.textContent = bbQty;
    if (qtyHidden) qtyHidden.value = bbQty;
    window._pdpSyncBuyNow();
  }
  bbMinus && on(bbMinus, 'click', () => updateBbQty(-1));
  bbPlus  && on(bbPlus,  'click', () => updateBbQty(1));

  // ── Sticky bar qty stepper ──
  let sbQty = 1;
  const sbMinus = document.getElementById('sbMinus');
  const sbPlus  = document.getElementById('sbPlus');
  const sbNum   = document.getElementById('sbNum');
  sbMinus && on(sbMinus, 'click', () => { sbQty = Math.max(1, sbQty - 1); if (sbNum) sbNum.textContent = sbQty; });
  sbPlus  && on(sbPlus,  'click', () => { sbQty++; if (sbNum) sbNum.textContent = sbQty; });

  // Sticky bar ATC button — adds current variant via AJAX
  const sbAtcBtn = document.getElementById('sbAtcBtn');
  if (sbAtcBtn) {
    on(sbAtcBtn, 'click', async () => {
      const v = resolveVariant();
      if (!v || !v.available) return;
      sbAtcBtn.textContent = 'Adding…';
      try {
        const payload = { id: v.id, quantity: sbQty };
        // Attach the subscription selling plan if Subscribe & Save is active → FBA/MCF recurring
        const plan = window._sasSellingPlan ? window._sasSellingPlan() : '';
        if (plan) payload.selling_plan = plan;
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });
        sbAtcBtn.textContent = '✓ Added!';
        setTimeout(() => { sbAtcBtn.innerHTML = `Add to Cart — <span id="sbAtcPrice">${formatMoney(v.price)}</span>`; }, 1800);
        // Refresh cart drawer count
        if (window.CartDrawer) window.CartDrawer.fetchAndRender?.();
      } catch (e) {
        sbAtcBtn.textContent = 'Error — try again';
        setTimeout(() => { sbAtcBtn.innerHTML = `Add to Cart — <span id="sbAtcPrice">${formatMoney(v.price)}</span>`; }, 2000);
      }
    });
  }

  // ── Main ATC button feedback ──
  const atcForm = document.getElementById('productForm');
  if (atcForm) {
    on(atcForm, 'submit', e => {
      const btn = document.getElementById('bbAtcBtn');
      if (btn && !btn.disabled) {
        setTimeout(() => {
          const orig = btn.innerHTML;
          btn.innerHTML = '✓ Added to Cart!';
          btn.style.background = 'var(--color-natural-green)';
          setTimeout(() => { btn.innerHTML = orig; btn.style.background = ''; }, 2200);
        }, 200);
      }
    });
  }

  // ── Thumbnail gallery ──
  $$('.pdp-thumb').forEach(thumb => {
    on(thumb, 'click', () => {
      $$('.pdp-thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const mainImg = document.getElementById('mainImg');
      if (mainImg && thumb.dataset.img) mainImg.src = thumb.dataset.img;
    });
    on(thumb, 'keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); thumb.click(); }
    });
  });

  // ── Sticky bottom bar — show when buy box scrolls out of view ──
  const stickyBar = document.getElementById('pdpStickyBar');
  const buyBox    = document.getElementById('buyBox');
  if (stickyBar && buyBox) {
    const io = new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('show', !entry.isIntersecting);
    }, { threshold: 0 });
    io.observe(buyBox);
  }

  // ── Q&A accordion ──
  $$('.pdp-qa-q').forEach(q => {
    on(q, 'click', () => {
      const item = q.closest('.pdp-qa-item');
      const wasOpen = item.classList.contains('open');
      $$('.pdp-qa-item.open').forEach(i => i.classList.remove('open'));
      if (!wasOpen) item.classList.add('open');
    });
    on(q, 'keydown', e => { if (e.key === 'Enter') q.click(); });
  });

  // ── Helpful buttons ──
  $$('[data-helpful]').forEach(btn => {
    on(btn, 'click', () => {
      const match = btn.textContent.match(/\((\d+)\)/);
      if (match) {
        const count = parseInt(match[1]) + 1;
        btn.textContent = btn.textContent.replace(/\(\d+\)/, `(${count})`);
        btn.style.borderColor = 'var(--color-natural-green)';
        btn.style.color = 'var(--color-natural-green)';
        btn.disabled = true;
      }
    });
  });

  // ── Legacy tabs / FAQ (still in layout for old template sections) ──
  $$('.tab-btn').forEach(btn => {
    on(btn, 'click', () => {
      const tabId = btn.dataset.tab;
      $$('.tab-btn').forEach(b => b.classList.remove('is-active'));
      $$('.tab-panel').forEach(p => p.classList.remove('is-active'));
      btn.classList.add('is-active');
      $(`[data-tab-panel="${tabId}"]`)?.classList.add('is-active');
    });
  });
  $$('.faq-question').forEach(btn => {
    on(btn, 'click', () => {
      const item = btn.closest('.faq-item');
      const answer = item.querySelector('.faq-answer');
      const isOpen = item.classList.contains('is-open');
      $$('.faq-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        i.querySelector('.faq-answer').style.maxHeight = '0';
      });
      if (!isOpen) { item.classList.add('is-open'); answer.style.maxHeight = answer.scrollHeight + 'px'; }
    });
  });

  // ── Initialize Subscribe & Save ──
  initSubscribeAndSave();

  // ── Initialize UI with the current (server-rendered) variant ──
  applyVariant(resolveVariant());
}

/* -------------------------------------------------------------------------
   Problem-Solution Slider
   ------------------------------------------------------------------------- */
function initProblemSolution() {
  const section = $('.problem-solution-section');
  if (!section) return;

  // Just ensure the section animates in nicely — layout is static CSS
}

/* -------------------------------------------------------------------------
   Cart Page — qty +/- buttons (full page, not the drawer)
   ------------------------------------------------------------------------- */
function initCartPage() {
  // Only run on the /cart page
  const cartForm = document.querySelector('form[action="/cart"]');
  if (!cartForm) return;

  let _cartBusy = false;

  cartForm.addEventListener('click', async e => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn || _cartBusy) return;

    const key     = btn.dataset.key;
    const change  = parseInt(btn.dataset.qtyChange, 10);
    const qtyEl   = btn.closest('.quantity-selector')?.querySelector('.qty-value');
    if (!qtyEl || !key) return;

    const current = parseInt(qtyEl.textContent, 10);
    if (isNaN(current)) return;
    const newQty  = Math.max(0, current + change);

    // Optimistic display update
    qtyEl.textContent = newQty;
    btn.disabled = true;
    _cartBusy = true;

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      if (res.ok) {
        // Reload so all Liquid-rendered prices (line price, order summary, subtotal) update correctly
        window.location.reload();
      } else {
        qtyEl.textContent = current;
        btn.disabled = false;
        _cartBusy = false;
      }
    } catch (err) {
      console.warn('Cart page qty error:', err);
      qtyEl.textContent = current;
      btn.disabled = false;
      _cartBusy = false;
    }
  });
}

/* -------------------------------------------------------------------------
   Newsletter Form
   ------------------------------------------------------------------------- */
function initNewsletter() {
  $$('.newsletter-form').forEach(form => {
    on(form, 'submit', async e => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const btn = form.querySelector('button[type="submit"]');
      if (!input?.value) return;

      const orig = btn.innerHTML;
      btn.innerHTML = '<span class="loader"></span>';
      btn.disabled = true;

      // Shopify handles newsletter via /contact form submission
      try {
        const fd = new FormData();
        fd.append('form_type', 'customer');
        fd.append('utf8', '✓');
        fd.append('contact[email]', input.value);
        fd.append('contact[tags]', 'newsletter');

        await fetch('/', { method: 'POST', body: fd });
        btn.innerHTML = '✓ Subscribed!';
        input.value = '';
        setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 3000);
      } catch {
        btn.innerHTML = orig;
        btn.disabled = false;
      }
    });
  });
}

/* -------------------------------------------------------------------------
   Announcement bar ticker
   ------------------------------------------------------------------------- */
function initAnnouncementTicker() {
  const ticker = $('.announcement-bar__ticker');
  if (!ticker) return;
  // Messages are static in HTML; CSS handles overflow hiding.
  // Could add JS-based marquee if needed.
}

/* -------------------------------------------------------------------------
   Paw print parallax (subtle)
   ------------------------------------------------------------------------- */
function initPawParallax() {
  const paws = $$('.paw-float');
  if (!paws.length) return;

  on(window, 'scroll', () => {
    const scrollY = window.scrollY;
    paws.forEach((paw, i) => {
      const speed = 0.05 + (i * 0.01);
      paw.style.transform = `translateY(${scrollY * speed * (i % 2 === 0 ? 1 : -1)}px)`;
    });
  }, { passive: true });
}

/* -------------------------------------------------------------------------
   Search overlay
   ------------------------------------------------------------------------- */
function initSearch() {
  const overlay  = $('#search-overlay');
  const panel    = $('#search-panel');
  const input    = $('#search-input');
  const results  = $('#search-results');
  const openBtns = $$('[data-search-open]');
  const closeBtn = $('#search-close');
  if (!overlay) return;

  function open() {
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => input && input.focus());
  }
  function close() {
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    if (input) input.value = '';
    if (results) results.classList.remove('show');
  }

  openBtns.forEach(btn => on(btn, 'click', open));
  if (closeBtn) on(closeBtn, 'click', close);
  on(overlay, 'click', e => { if (e.target === overlay) close(); });
  on(document, 'keydown', e => { if (e.key === 'Escape') close(); });

  if (input) {
    on(input, 'input', debounce(() => {
      if (!results) return;
      const q = input.value.trim();
      if (q.length < 1) { results.classList.remove('show'); return; }
      // Shopify Predictive Search API
      fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4`)
        .then(r => r.json())
        .then(data => {
          const items = data.resources?.results?.products || [];
          if (!items.length) { results.classList.remove('show'); return; }
          results.innerHTML = items.map(p => `
            <a class="search-result" href="${p.url}">
              <div class="sr-thumb"><img src="${p.featured_image?.url || ''}" alt="${p.title}" loading="lazy"></div>
              <div>
                <div class="sr-name">${p.title}</div>
                <div class="sr-meta">${formatMoney(p.price)} · ${p.vendor}</div>
              </div>
            </a>`).join('');
          results.classList.add('show');
        })
        .catch(() => results.classList.remove('show'));
    }, 300));
  }

  $$('.search-chip[data-query]').forEach(chip => {
    on(chip, 'click', () => {
      if (input) { input.value = chip.dataset.query; input.dispatchEvent(new Event('input')); input.focus(); }
    });
  });
}

/* -------------------------------------------------------------------------
   Initialize all
   ------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  // Reveal FIRST so content is never left invisible if a later init throws.
  // Each init is isolated so one failure can't break the whole page.
  const safe = (fn) => { try { fn(); } catch (e) { console.warn('[theme] init error:', e); } };
  safe(initScrollReveal);
  safe(initHeader);
  safe(initMobileMenu);
  safe(() => CartDrawer.init());
  safe(initProductGallery);
  safe(initProductPage);
  safe(initCartPage);
  safe(initNewsletter);
  safe(initAnnouncementTicker);
  safe(initPawParallax);
  safe(initSearch);
});


/* ===== PORTED: footer husky peek-up on scroll ===== */
(function () {
  function initHuskyPeek() {
    var husky = document.querySelector('.tsf-husky');
    var footer = document.querySelector('footer');
    if (!husky || !footer || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      husky.classList.toggle('peek', entries[0].isIntersecting);
    }, { threshold: 0.35 });
    io.observe(footer);
  }
  if (document.readyState !== 'loading') initHuskyPeek();
  else document.addEventListener('DOMContentLoaded', initHuskyPeek);
})();


/* ===== PORTED: UGC reel + infographic accordion ===== */
(function () {
  function initPortedPDP() {
    // UGC cards: hover plays (Ken Burns + progress), leave pauses; click opens modal
    document.querySelectorAll('.ugc-card').forEach(function (card) {
      var thumb = card.querySelector('.ugc-thumb');
      if (thumb && !thumb.querySelector('.ugc-progress')) {
        var live = document.createElement('span'); live.className = 'ugc-live'; live.textContent = 'LIVE';
        var prog = document.createElement('div'); prog.className = 'ugc-progress';
        thumb.appendChild(live); thumb.appendChild(prog);
      }
      card.addEventListener('mouseenter', function () { card.classList.add('playing'); });
      card.addEventListener('mouseleave', function () { card.classList.remove('playing'); });
      card.addEventListener('click', function () {
        var modal = document.getElementById('ugcModal'); if (!modal) return;
        var h = document.getElementById('ugcModalHandle'); if (h) h.textContent = card.dataset.handle || '';
        var q = document.getElementById('ugcModalQuote'); if (q) q.textContent = '"' + (card.dataset.quote || '') + '"';
        var img = document.getElementById('ugcModalImg'); if (img && card.dataset.img) img.src = card.dataset.img;
        modal.classList.add('open');
      });
    });

    var track = document.getElementById('ugcTrack');
    var prev = document.getElementById('ugcPrev');
    var next = document.getElementById('ugcNext');
    if (track && prev) prev.addEventListener('click', function () { track.scrollBy({ left: -408, behavior: 'smooth' }); });
    if (track && next) next.addEventListener('click', function () { track.scrollBy({ left: 408, behavior: 'smooth' }); });

    var modal = document.getElementById('ugcModal');
    var close = document.getElementById('ugcModalClose');
    if (close) close.addEventListener('click', function () { modal.classList.remove('open'); });
    if (modal) modal.addEventListener('click', function (e) { if (e.target === modal) modal.classList.remove('open'); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && modal) modal.classList.remove('open'); });

    var mvp = document.getElementById('mainVideoPlayBtn');
    if (mvp && modal) mvp.addEventListener('click', function () {
      var h = document.getElementById('ugcModalHandle'); if (h) h.textContent = 'TailStickle — Official Reel';
      var q = document.getElementById('ugcModalQuote'); if (q) q.textContent = 'See how TailStickle cleans muddy paws in under 30 seconds.';
      modal.classList.add('open');
    });

    // Infographic benefits accordion (animated max-height)
    document.querySelectorAll('.ig-accordion-item').forEach(function (item) {
      var a = item.querySelector('.ig-acc-a');
      if (a) a.style.maxHeight = item.classList.contains('open') ? a.scrollHeight + 'px' : '0';
      var q = item.querySelector('.ig-acc-q');
      if (q) q.addEventListener('click', function () {
        var wasOpen = item.classList.contains('open');
        document.querySelectorAll('.ig-accordion-item').forEach(function (i) {
          i.classList.remove('open');
          var ia = i.querySelector('.ig-acc-a'); if (ia) ia.style.maxHeight = '0';
        });
        if (!wasOpen) { item.classList.add('open'); if (a) a.style.maxHeight = a.scrollHeight + 'px'; }
      });
    });
  }
  if (document.readyState !== 'loading') initPortedPDP();
  else document.addEventListener('DOMContentLoaded', initPortedPDP);
})();


/* ===== PORTED: media gallery (arrows + video + zoom) ===== */
(function () {
  function initMediaGallery() {
    var wrap = document.getElementById('mainImgWrap');
    var thumbsTrack = document.getElementById('pdpThumbs');
    if (!wrap) return;
    var mainImg = document.getElementById('mainImg');
    var videoWrap = document.getElementById('mainVideoWrap');
    var thumbs = thumbsTrack ? Array.prototype.slice.call(thumbsTrack.querySelectorAll('.pdp-thumb')) : [];
    var idx = 0;

    function pauseAll() {
      if (!videoWrap) return;
      videoWrap.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (e) {} });
      videoWrap.querySelectorAll('.pdp-video-slot').forEach(function (s) { s.classList.remove('active'); });
    }
    function activate(i) {
      if (!thumbs.length) return;
      idx = (i + thumbs.length) % thumbs.length;
      var t = thumbs[idx];
      thumbs.forEach(function (x) { x.classList.remove('active'); });
      t.classList.add('active');
      try { t.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' }); } catch (e) {}
      if (t.dataset.type === 'video') {
        pauseAll();
        if (mainImg) mainImg.style.visibility = 'hidden';
        if (videoWrap) {
          videoWrap.classList.add('show');
          var slot = videoWrap.querySelector('.pdp-video-slot[data-media-id="' + t.dataset.mediaId + '"]');
          if (slot) { slot.classList.add('active'); var v = slot.querySelector('video'); if (v) { try { v.play(); } catch (e) {} } }
        }
      } else {
        pauseAll();
        if (videoWrap) videoWrap.classList.remove('show');
        if (mainImg) { mainImg.style.visibility = 'visible'; if (t.dataset.img) mainImg.src = t.dataset.img; }
      }
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(i); });
      t.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(i); } });
    });

    var prev = document.getElementById('imgPrev'), next = document.getElementById('imgNext');
    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); activate(idx - 1); });
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); activate(idx + 1); });

    var tprev = document.getElementById('thumbPrev'), tnext = document.getElementById('thumbNext');
    if (tprev && thumbsTrack) tprev.addEventListener('click', function () { thumbsTrack.scrollBy({ left: -170, behavior: 'smooth' }); });
    if (tnext && thumbsTrack) tnext.addEventListener('click', function () { thumbsTrack.scrollBy({ left: 170, behavior: 'smooth' }); });

    // Zoom lightbox (image mode only)
    if (mainImg) {
      mainImg.addEventListener('click', function () {
        if (thumbs[idx] && thumbs[idx].dataset.type === 'video') return;
        var ov = document.createElement('div');
        ov.className = 'pdp-zoom-overlay';
        ov.innerHTML = '<button class="pdp-zoom-close" aria-label="Close">✕</button><img alt="">';
        ov.querySelector('img').src = mainImg.src;
        document.body.appendChild(ov);
        document.body.style.overflow = 'hidden';
        function closeZoom() { ov.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', onEsc); }
        function onEsc(e) { if (e.key === 'Escape') closeZoom(); }
        ov.addEventListener('click', function (e) { if (e.target === ov || e.target.classList.contains('pdp-zoom-close')) closeZoom(); });
        document.addEventListener('keydown', onEsc);
      });
    }
  }
  if (document.readyState !== 'loading') initMediaGallery();
  else document.addEventListener('DOMContentLoaded', initMediaGallery);
})();


/* ===== PORTED: collection card hover image cycle ===== */
(function () {
  function initCardHover() {
    document.querySelectorAll('[data-product-card]').forEach(function (card) {
      var imgs = card.querySelectorAll('.product-card__image');
      var dots = card.querySelectorAll('.product-card__dot');
      if (imgs.length < 2) return;
      var i = 0, timer = null;
      function show(n) {
        imgs[i].classList.remove('is-active');
        if (dots[i]) dots[i].classList.remove('active');
        i = (n + imgs.length) % imgs.length;
        imgs[i].classList.add('is-active');
        if (dots[i]) dots[i].classList.add('active');
      }
      function start() {
        if (timer) return;
        timer = setInterval(function () { show(i + 1); }, 900);
      }
      function stop() {
        clearInterval(timer); timer = null;
        show(0); // reset to the first image
      }
      card.addEventListener('mouseenter', start);
      card.addEventListener('mouseleave', stop);
    });
  }
  if (document.readyState !== 'loading') initCardHover();
  else document.addEventListener('DOMContentLoaded', initCardHover);
})();
